import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAdminAuth } from '@/auth/use-admin-auth';
import { VerseConnectionManager } from './VerseConnectionManager';
import {
  DEFAULT_VERSE_ALIAS,
  gatewayIdForVerseEnv,
  gatewayUrlOf,
  resolveVerseEntry,
} from '@rb/shared/admin-verses';
import {
  GATEWAY_STORAGE_KEY,
  resolveInitialGatewayOverride,
  useActiveVerseStore,
} from './active-verse.store';

const STORAGE_KEY = 'rb-admin-app:active-verse';

export function VerseSessionInitializer() {
  const { account, getAccessToken } = useAdminAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Refs so the effect re-runs only on account change — manager is per-account, not per-URL.
  // Without this, every page navigation (= searchParams change) tore down the manager and
  // the `initialized` guard prevented re-creation, leaving the verse switcher disabled forever.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    if (!account) return;
    const manager = new VerseConnectionManager(account, getAccessToken);
    const store = useActiveVerseStore.getState();
    store.setManager(manager);
    store.setConnectionError(null);

    const desired =
      searchParamsRef.current.get('verse') ??
      window.localStorage.getItem(STORAGE_KEY) ??
      DEFAULT_VERSE_ALIAS;
    const verse = resolveVerseEntry(desired);
    const verseGateway = gatewayIdForVerseEnv(verse.env);
    // 명시적 ?gateway= 또는 저장된 override 가 verse env 기본값과 다르면 override 로 채택.
    const rawGateway =
      searchParamsRef.current.get('gateway') ?? window.localStorage.getItem(GATEWAY_STORAGE_KEY);
    const override = resolveInitialGatewayOverride(rawGateway, verseGateway);
    const effectiveGateway = override ?? verseGateway;
    store.setGatewayOverride(override);
    store.setSwitching(true);
    manager
      .switchTo(verse.alias, gatewayUrlOf(effectiveGateway))
      .then(() => {
        useActiveVerseStore.getState().setActive(verse.verseId);
        useActiveVerseStore.getState().setConnectionError(null);
        window.localStorage.setItem(STORAGE_KEY, verse.alias);
        if (override) window.localStorage.setItem(GATEWAY_STORAGE_KEY, override);
        else window.localStorage.removeItem(GATEWAY_STORAGE_KEY);
        setSearchParamsRef.current(
          (sp) => {
            const next = new URLSearchParams(sp);
            next.set('verse', verse.alias);
            next.set('gateway', effectiveGateway);
            return next;
          },
          { replace: true }
        );
      })
      .catch((err: unknown) => {
        console.error('[VerseSessionInitializer] initial switch failed:', err);
        useActiveVerseStore
          .getState()
          .setConnectionError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => useActiveVerseStore.getState().setSwitching(false));

    return () => {
      manager.disconnect().catch(() => undefined);
      useActiveVerseStore.getState().setManager(null);
      useActiveVerseStore.getState().setActive(null);
      useActiveVerseStore.getState().setConnectionError(null);
    };
  }, [account, getAccessToken]);

  return null;
}
